import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { ArrowBack, HomeOutlined, SearchOffOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const NotFound = () => {
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 3,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Background decorative circles */}
            <Box
                sx={{
                    position: "absolute",
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: "var(--primary-light-color)",
                    top: -180,
                    right: -120,
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    width: 250,
                    height: 250,
                    borderRadius: "50%",
                    background: "var(--primary-light-color)",
                    bottom: -120,
                    left: -80,
                }}
            />

            <Box
                sx={{
                    width: "100%",
                    maxWidth: 850,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 5, md: 10 },
                    flexDirection: { xs: "column", md: "row" },
                    position: "relative",
                    zIndex: 1,
                }}
            >
                {/* 404 Illustration */}
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: 260, sm: 320, md: 380 },
                        height: { xs: 220, sm: 270, md: 320 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {/* Main 404 */}
                    <Typography
                        sx={{
                            fontSize: { xs: "110px", sm: "140px", md: "170px" },
                            fontWeight: 900,
                            lineHeight: 1,
                            letterSpacing: "-10px",
                            color: "var(--primary-color)",
                            userSelect: "none",
                        }}
                    >
                        404
                    </Typography>

                    {/* Floating search icon */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 5,
                            right: { xs: 0, md: 10 },
                            width: 65,
                            height: 65,
                            borderRadius: "18px",
                            background: "var(--primary-dark-color)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transform: "rotate(12deg)",
                            boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
                        }}
                    >
                        <SearchOffOutlined sx={{ fontSize: 34 }} />
                    </Box>

                    {/* Small decorative dot */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 35,
                            left: 15,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "var(--primary-color)",
                        }}
                    />

                    {/* Decorative line */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 55,
                            right: 30,
                            width: 70,
                            height: 8,
                            borderRadius: 10,
                            background: "var(--primary-light-color)",
                            transform: "rotate(-25deg)",
                        }}
                    />
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        maxWidth: 430,
                        textAlign: { xs: "center", md: "left" },
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 0.8,
                            borderRadius: 20,
                            background: "var(--primary-light-color)",
                            color: "var(--primary-dark-color)",
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "var(--primary-color)",
                            }}
                        />

                        <Typography
                            variant="body2"
                            className="fw-bold"
                        >
                            PAGE NOT FOUND
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            fontSize: {
                                xs: "32px",
                                sm: "38px",
                                md: "44px",
                            },
                            lineHeight: 1.15,
                            fontWeight: 800,
                            color: "var(--primary-dark-color)",
                            mb: 2,
                        }}
                    >
                        Oops! Looks like
                        <br />
                        you’re lost.
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "16px",
                            lineHeight: 1.7,
                            color: "rgba(0,0,0,0.55)",
                            mb: 4,
                            maxWidth: 400,
                        }}
                    >
                        The page you're looking for doesn't exist or
                        may have been moved. Let's get you back to
                        somewhere useful.
                    </Typography>

                    {/* Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            flexWrap: "wrap",
                            justifyContent: {
                                xs: "center",
                                md: "flex-start",
                            },
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<HomeOutlined />}
                            onClick={() => navigate("/")}
                            sx={{
                                px: 3,
                                py: 1.4,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: "15px",
                                fontWeight: 700,
                                background: "var(--primary-color)",
                                boxShadow:
                                    "0 10px 25px rgba(71,170,167,0.25)",
                                "&:hover": {
                                    background:
                                        "var(--primary-dark-color)",
                                },
                            }}
                        >
                            Back to Home
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                            sx={{
                                px: 3,
                                py: 1.4,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: "15px",
                                fontWeight: 700,
                                color: "var(--primary-dark-color)",
                                borderColor:
                                    "var(--primary-dark-light-color)",
                                "&:hover": {
                                    borderColor:
                                        "var(--primary-color)",
                                    background:
                                        "var(--primary-light-color)",
                                },
                            }}
                        >
                            Go Back
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default NotFound;